import { useEffect, useState } from "react";
import type { EmailMeta } from "@/api/email";
import { fetchMeta } from "@/hooks/useEmailMeta";
import { PERMISSION } from "@/lib/permissions";
import { useAuthStore } from "@/stores/auth.store";
import { useProjectStore } from "@/stores/project.store";

const EMAIL_PROJECT_TYPES = ["cfo", "m_and_a"];
const NONE: EmailMeta[] = [];

/**
 * Every project whose Email module the user may open, with its meta — as the
 * backend answers each project's own `/email/meta`. Feeds the "All / CFO /
 * M&A" filter: "All" is these projects' own calls, merged here; the backend
 * never serves two projects in one answer.
 */
export function useEmailProjects(): { projects: EmailMeta[]; loading: boolean } {
  const user = useAuthStore((s) => s.user);
  const userId = user?.id ?? "";
  const all = useProjectStore((s) => s.projects);
  // Asked only where the role opens Email (outreach.view and contacts.view):
  // a project the user may not open is never even requested.
  const slugs = all
    .filter((p) => p.is_active !== false && EMAIL_PROJECT_TYPES.includes(p.project_type_id))
    .filter((p) => {
      if (user?.is_superadmin) return true;
      const permissions = user?.memberships.find((m) => m.project_id === p.id)?.role?.permissions ?? [];
      return permissions.includes(PERMISSION.OUTREACH_VIEW) && permissions.includes(PERMISSION.CONTACTS_VIEW);
    })
    .map((p) => p.slug)
    .sort()
    .join(",");
  const [state, setState] = useState<{ key: string; projects: EmailMeta[] }>({ key: "", projects: NONE });
  const key = `${userId}:${slugs}`;

  useEffect(() => {
    if (!userId || !slugs) return;
    let cancelled = false;
    void Promise.allSettled(slugs.split(",").map((slug) => fetchMeta(userId, slug))).then((results) => {
      if (cancelled) return;
      const projects = results
        .filter((r): r is PromiseFulfilledResult<EmailMeta> => r.status === "fulfilled")
        .map((r) => r.value)
        .sort((a, b) => a.project.name.localeCompare(b.project.name));
      setState({ key, projects });
    });
    return () => {
      cancelled = true;
    };
  }, [userId, slugs, key]);

  return { projects: state.key === key ? state.projects : NONE, loading: state.key !== key && !!slugs };
}
